import React, { ReactNode, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";
import Login from "./components/Login";
import { useAuth } from "./contexts/AuthContext";
import theme from "./theme";
import DashboardLayout from "./components/DashboardLayout";
import Lam1ProductionTabs from "./components/pages/Lamination1/Lam1ProductionTabs";
import SettingsPage from "./components/pages/Settings/SettingsPage";
import FoamProductionTabs from "./components/pages/Foam/FoamProductionTabs";
import MainOverviewPage from "./components/pages/Overview/MainOverviewPage";

// Operator “dummy” pages (add these files below)
import Lam1OperatorPage from "./components/pages/Lamination1Operator/Lam1OperatorPage";
import FoamOperatorPage from "./components/pages/FoamOperator/FoamOperatorPage";
import InventoryForecastPage from "./components/pages/Inventory/InventoryForecastPage";
import Lam2ProductionTabs from "./components/pages/Lamination2/Lam2ProductionTabs";
import AccountPage from "./components/pages/Account/AccountPage";
import ManufacturingPage from "./components/pages/Manufacturing/ManufacturingPage";
import SalesOrdersPage from "./components/pages/SalesOrders/SalesOrdersPage";
import ConnectorsPage from "./components/pages/Connectors/ConnectorsPage";
import AlertsPage from "./components/pages/Alerts/AlertsPage";
import ReportsPage from "./components/pages/Reports/ReportsPage";
import { loadLastRoute, saveLastRoute } from "./utils/uiPrefs";
// import SettingsOperatorPage from "./components/pages/Settings/SettingsOperatorPage";

interface ProtectedRouteProps {
  children: ReactNode;
}

// Records the current page so the next visit resumes here.
const RouteMemory: React.FC = () => {
  const { pathname } = useLocation();
  const { isAuthenticated, role } = useAuth();

  useEffect(() => {
    if (isAuthenticated) saveLastRoute(pathname, role);
  }, [pathname, isAuthenticated, role]);

  return null;
};

// Logged-in gate (any role)
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Admin-only gate
// const AdminRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
//   const { isAuthenticated, role } = useAuth();
//   if (!isAuthenticated) return <Navigate to="/login" replace />;
//   if (role !== "admin") return <Navigate to="/lamination1-operator" replace />;
//   return <>{children}</>;
// };

// // Operator-only gate (explicit operator, not admin)
// const OperatorOnly: React.FC<ProtectedRouteProps> = ({ children }) => {
//   const { isAuthenticated, role } = useAuth();
//   if (!isAuthenticated) return <Navigate to="/login" replace />;
//   if (role !== "operator") return <Navigate to="/main" replace />;
//   return <>{children}</>;
// };

// ✅ Safe to call useAuth within AuthProvider
const AppRoutes: React.FC = () => {
  const { isAuthenticated, role, hasAdminAccess } = useAuth();

  return (
    <>
      <RouteMemory />
      <Routes>
        {/* Root: resume the last page visited (overview by default for admins,
            lamination 1 for operators); guests go to /login */}
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <Navigate to={loadLastRoute(role)} replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* Admin routes (unchanged UI) */}
        {hasAdminAccess && (
          <Route
            path="/main"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <MainOverviewPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
        )}

        {hasAdminAccess && (
          <Route
            path="/manufacturing"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ManufacturingPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
        )}

        {/* Parked: the route resolves, but the nav entry is disabled until
            what belongs on it is agreed. */}
        {hasAdminAccess && (
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ReportsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
        )}

        {hasAdminAccess && (
          <Route
            path="/inventory"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <InventoryForecastPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
        )}
        {hasAdminAccess && (
          <Route
            path="/sales-orders"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <SalesOrdersPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
        )}
        <Route
          path="/lamination1"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                {hasAdminAccess ? <Lam1ProductionTabs /> : <Lam1OperatorPage />}
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/lamination2"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                {hasAdminAccess ? <Lam2ProductionTabs /> : <></>}
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/foam"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <FoamProductionTabs />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <SettingsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        {hasAdminAccess && (
          <Route
            path="/connectors"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ConnectorsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
        )}
        {hasAdminAccess && (
          <Route
            path="/alerts"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <AlertsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
        )}
        {/* Operator-only routes (dummy pages for now) */}
        {/* <Route
          path="/lamination1-operator"
          element={
            <OperatorOnly>
              <DashboardLayout>
                <Lam1OperatorPage />
              </DashboardLayout>
            </OperatorOnly>
          }
        />
        <Route
          path="/foam-operator"
          element={
            <OperatorOnly>
              <DashboardLayout>
                <FoamOperatorPage />
              </DashboardLayout>
            </OperatorOnly>
          }
        /> */}
        {/* <Route
          path="/settings-operator"
          element={
            <OperatorOnly>
              <DashboardLayout>
                <SettingsOperatorPage />
              </DashboardLayout>
            </OperatorOnly>
          }
        /> */}
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <AccountPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppRoutes />
      </Router>
    </ThemeProvider>
  );
}

export default App;
